<template>
  <div id="pageStretcher">
    <TheHeader />

    <!-- Mobile Sidebar Overlay -->
    <transition name="overlay">
      <div
        v-if="isOpenMobileSideBar && shouldUseMobileSidebar"
        class="sidebar-overlay"
        @click="closeMobileSidebar"
      ></div>
    </transition>

    <div class="app-body">
      <v-container
        :fluid="isFluidContainer"
        class="container"
        :class="{ 'rest-layout': isLoginPage }"
      >
        <v-row :class="rowClasses">
          <!-- Sidebar Column -->
          <v-col
            v-if="shouldShowSidebar"
            :class="sidebarClasses"
            ref="sidebarElement"
          >
            <router-view name="sideBar" />
          </v-col>

          <!-- Mobile Sidebar (Fixed Position) - Always render but control visibility -->
          <v-col
            v-if="shouldShowSidebar && shouldUseMobileSidebar"
            :class="[
              sidebarClasses,
              { 'sidebar-hidden': !isOpenMobileSideBar },
            ]"
            ref="mobileSidebarElement"
            key="mobile-sidebar"
          >
            <transition name="sidebar-slide">
              <div v-show="isOpenMobileSideBar" class="sidebar-content">
                <router-view name="sideBar" />
              </div>
            </transition>
          </v-col>

          <!-- Main Content Column -->
          <v-col :class="['main', { 'full-main': !shouldShowSidebar }]">
            <main v-if="isMainContentShowing" id="mainapp">
              <router-view />
            </main>
          </v-col>
        </v-row>
      </v-container>

      <router-view name="resetPassPage" />
    </div>

    <Toast />
  </div>
</template>

<script>
import TheHeader from "./components/layout/TheHeader.vue";
import { mapGetters, mapActions } from "vuex";
import Toast from "./components/UI/Toast.vue";

export default {
  name: "App",
  components: {
    TheHeader,
    Toast,
  },

  data() {
    return {
      timerToAutoLogoutDesktop: null,
      timerToAutoLogoutMobile: null,
      minute: 60000,
    };
  },

  computed: {
    ...mapGetters({
      isUserLogin: "AuthModule/isAuthenticated",
      getUserLang: "AuthModule/getUserLang",
      isMobile: "isMobile",
      isLaptop: "isLaptop",
      isTablet: "isTablet",
      isDesktop: "isDesktop",
      isOpenMobileSideBar: "isOpenMobileSideBar",
    }),

    // Route-based computed properties
    currentRouteName() {
      return this.$route.name;
    },

    isLoginPage() {
      return this.currentRouteName === "loginPage";
    },

    isHomePage() {
      return this.currentRouteName === "home";
    },

    isCategoryPage() {
      return this.currentRouteName === "Category";
    },

    isAgentSalesPage() {
      return this.currentRouteName === "AgentSales";
    },

    // Layout computed properties
    isFluidContainer() {
      return this.isCategoryPage || this.isHomePage;
    },

    shouldShowSidebar() {
      // The Cart sidebar (rendered on the home & Category pages) is hidden,
      // so collapse its column to reclaim the empty space.
      if (this.isHomePage || this.isCategoryPage) {
        return false;
      }
      return true;
    },

    shouldUseMobileSidebar() {
      return this.isMobile || this.isTablet || this.isLaptop;
    },

    rowClasses() {
      const classes = [];

      if (this.isCategoryPage || this.isHomePage || this.isAgentSalesPage) {
        classes.push("flex-row-reverse");
      }

      if (this.isHomePage) {
        classes.push("flex-wrap-nowrap");
      }

      return classes;
    },

    sidebarClasses() {
      const classes = [];

      if (this.isLoginPage) {
        classes.push("nav-login");
      } else if (this.isHomePage) {
        classes.push("side-bar-cart");
      }

      classes.push(
        this.shouldUseMobileSidebar && this.isOpenMobileSideBar
          ? "mobile-side-bar"
          : "side-bar"
      );

      return classes;
    },

    isMainContentShowing() {
      return true;
    },
  },

  watch: {
    "$store.state.AuthModule.token": {
      handler() {
        this.setLang();
      },
      immediate: true,
    },
  },

  created() {
    this.initializeApp();
  },

  mounted() {
    this.hideLoader();
    this.setLang();
  },

  unmounted() {
    this.cleanupEventListeners();
  },

  methods: {
    closeMobileSidebar() {
      this.$store.commit("setIsOpenMobileSideBar", false);
    },

    ...mapActions({
      logout: "AuthModule/logout",
    }),

    initializeApp() {
      this.setScreenSizes();
      this.setupEventListeners();
    },

    hideLoader() {
      const loader = document.getElementById("dolimo_loader");
      if (loader) {
        loader.style.display = "none";
      }
    },

    setupEventListeners() {
      window.addEventListener("resize", this.handleResize);
    },

    cleanupEventListeners() {
      window.removeEventListener("resize", this.handleResize);
      this.clearTimeouts();
    },

    handleResize() {
      this.setScreenSizes();
    },

    clearTimeouts() {
      if (this.timerToAutoLogoutDesktop) {
        clearTimeout(this.timerToAutoLogoutDesktop);
      }
      if (this.timerToAutoLogoutMobile) {
        clearTimeout(this.timerToAutoLogoutMobile);
      }
    },

    setLang() {
      if (this.isUserLogin) {
        this.$i18n.locale = this.getUserLang;
        document.dir = this.$i18n.messages[this.$i18n.locale].direction;
        document.documentElement.lang = this.$i18n.locale;
        this.$vuetify.locale.current = this.$i18n.locale;
      }
    },

    setScreenSizes() {
      const width = window.innerWidth;
      this.$store.commit("setIsDesktop", width >= 1500);
      this.$store.commit("setIsLaptop", width >= 993 && width < 1500);
      this.$store.commit("setIsTablet", width >= 602 && width < 993);
      this.$store.commit("setIsMobile", width < 602);
    },
  },
};
</script>

<style lang="scss">
@import "./assets/styles/main.scss";

// ===========================
// Global Layout Styles
// ===========================

#pageStretcher {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
}

.app-body {
  position: relative;
  flex: 1;
}

.v-container {
  padding: 0 !important;
  width: unset !important;

  @include media(laptop, desktop, smallLaptop, tablet) {
    max-width: 100% !important;
  }
}

.v-row {
  height: 100%;

  @include media(mobile) {
    flex-direction: column-reverse !important;
  }
}

.v-col {
  flex-basis: unset !important;
}

// ===========================
// Header Styles
// ===========================

header {
  position: sticky;
  top: 0;
  z-index: 99999 !important;
  transition: 0.2s;
}

.header-up {
  transform: translateY(-113px);
  transition: 0.2s;

  @include media(mobile) {
    transform: translateY(-93px);
  }
}

// ===========================
// Sidebar Styles
// ===========================

.side-bar {
  @include media(desktop) {
    position: sticky !important;
    top: 140px;
    max-width: 25% !important;
  }

  @include media(smallLaptop,laptop, tablet) {
    padding: 0 !important;
    max-width: 40% !important;
  }

  // @include media(tablet) {
  //   padding: 0 !important;
  //   max-width: 100% !important;
  // }

  @include media(mobile) {
    padding: 0 !important;
    max-width: 100% !important;

    &.col {
      padding: 0;
    }
  }
}

.side-bar-cart {
  height: 100%;
  box-shadow: 5px 0px 20px 0px rgb(0 0 0 / 5%);
  padding-bottom: 0 !important;
}

.mobile-side-bar {
  position: fixed;
  z-index: 3;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  height: 100%;
  padding: 5px !important;
  background: white;
  overflow-y: scroll;
  max-width: 400px !important;
  @include media(mobile) {
    max-width: 100% !important;
  }
}

.nav-login {
  top: 0 !important;
}

// ===========================
// Main Content Styles
// ===========================

.main {
  height: 100%;

  @include media(desktop) {
    max-width: 75% !important;
    flex: 0 0 75% !important;
  }
}

main {
  margin-top: 20px;

  @include media(mobile) {
    margin-top: unset;
  }
}

// When the sidebar is hidden, let the main column fill the full width
.full-main {
  max-width: 100% !important;
  flex: 1 0 100% !important;
}

// ===========================
// Layout Modifiers
// ===========================

.rest-layout {
  max-width: unset !important;

  .main {
    padding: 0 !important;
    max-width: unset !important;
    flex: 1 0 !important;
  }

  #mainapp {
    margin: 0 !important;
  }

  .side-bar {
    position: relative !important;
    display: grid;
  }
}

.flex-wrap-nowrap {
  flex-wrap: nowrap !important;
}

// ===========================
// Global Component Styles
// ===========================

.v-pagination {
  .v-btn__content {
    font-size: $smallTextSize;
  }
}

// ===========================
// Form Styles
// ===========================

input {
  max-width: 100% !important;
  box-sizing: border-box !important;

  &:focus-visible {
    outline: none !important;
  }

  // Hide number input arrows
  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  &[type="number"] {
    -moz-appearance: textfield;
  }

  &.dolimo-style {
    border: 1px solid lightgrey !important;
    border-radius: 10px !important;
    font-size: $smallTextSize !important;
    margin: 0 !important;
    padding-right: 10px !important;

    &:lang(en) {
      padding-right: 0;
      padding-left: 10px !important;
    }
  }
}

textarea {
  padding: 10px !important;
}

// ===========================
// Checkbox Styles
// ===========================

[type="checkbox"] {
  + span:not(.lever) {
    padding: unset;
    padding-right: 35px;

    &:lang(en) {
      padding-right: 0;
      padding-left: 35px;
    }
  }

  &.dolimo-style.filled-in {
    &:not(:checked) + span:not(.lever):after {
      border-radius: 7px;
      border: unset;
      box-shadow: 0 0 3px inset;
      right: 0;
    }

    &:checked + span:not(.lever):after {
      border-radius: 7px;
      border: unset;
      background-color: $primaryColor;
      top: 3px;
      right: 0;
    }

    &:checked + span:not(.lever):before {
      left: unset;
      right: 10px;

      &:lang(en) {
        top: 2px;
        left: 0;
      }
    }
  }
}

// ===========================
// Button Styles
// ===========================

.btn.dolimo-style {
  background: $primaryBackgroundButtonColor;
  font-size: $mediumTextSize;
  color: white !important;
}

// ===========================
// Menu Styles
// ===========================

.menu-collection {
  padding: 0 !important;
  border: unset !important;

  a {
    font-size: $mediumTextSize;
    border: unset !important;

    &.router-link-active {
      color: #73b235 !important;
      background-color: $primaryColorlight !important;
    }
  }
}

// ===========================
// Language Styles
// ===========================

.side-bar:lang(en),
.main:lang(en) {
  left: 0 !important;
}

.lang-container {
  align-items: center;
  display: flex;
  flex-direction: row;
  justify-content: end;

  .caret:lang(en) {
    left: unset !important;
    right: 0 !important;
  }

  .select-wrapper {
    width: 50px !important;

    input.select-dropdown:focus {
      border: unset !important;
      margin: 0 !important;
    }
  }

  input {
    border: unset !important;
    margin: 0 !important;
  }

  svg {
    right: unset !important;
    left: 0;
  }

  ul {
    padding: 0;
    text-align: center;

    span {
      color: #1c1c1c !important;
    }
  }
}

// ===========================
// Toast Styles
// ===========================

.toast-container {
  bottom: 0 !important;
}

.toast {
  color: white !important;

  &.success {
    background-color: #70b132;
  }

  &.error {
    background-color: rgb(240, 51, 51);
  }
}

// ===========================
// Typography
// ===========================

p {
  font-size: $mediumTextSize;
  color: $primaryTextColor;
}

html,
pre {
  font-family: "Rubik";
  box-sizing: border-box;
}

body {
  margin: 0;
  overflow-x: hidden;
  font-family: "Rubik";

  &.stop-scrolling {
    // overflow: hidden !important;
  }
}

// ===========================
// Scrollbar Styles
// ===========================

::-webkit-scrollbar {
  width: 5px;
  height: 5px;

  &-button {
    height: 5%;
  }

  &-track {
    box-shadow: inset 0 0 5px grey;
    border-radius: 10px;
  }

  &-thumb {
    background: linear-gradient(
      0deg,
      rgba(109, 17, 6, 1) 1%,
      rgba(255, 230, 173, 1) 100%
    );
    border-radius: 10px;
    min-height: 40px !important;

    &:hover {
      background: rgba(255, 230, 173, 1);
    }
  }
}

// ===========================
// Utility Classes
// ===========================

* {
  touch-action: manipulation;
}

.sidebar-hidden {
  display: none !important;
}

.sidebar-content {
  width: 100%;
  height: 100%;
}

.sidebar-slide-enter-active,
.sidebar-slide-leave-active {
  transition: all 0.4s cubic-bezier(0.25, 0.8, 0.25, 1);
}

.sidebar-slide-enter-from {
  transform: translateX(100%);
  opacity: 0;
}

.sidebar-slide-leave-to {
  transform: translateX(100%);
  opacity: 0;
}

.sidebar-slide-enter-to,
.sidebar-slide-leave-from {
  transform: translateX(0);
  opacity: 1;
}

// Overlay animation
.overlay-enter-active,
.overlay-leave-active {
  transition: opacity 0.3s ease;
}

.overlay-enter-from,
.overlay-leave-to {
  opacity: 0;
}

.overlay-enter-to,
.overlay-leave-from {
  opacity: 1;
}

// Add overlay styles
.sidebar-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 2;
  cursor: pointer;
}

// Update mobile sidebar to ensure smooth animation
.mobile-side-bar {
  position: fixed;
  z-index: 3;
  top: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  height: 100%;
  padding: 5px !important;
  background: white;
  overflow-y: auto;
  max-width: 400px !important;
  box-shadow: -5px 0 15px rgba(0, 0, 0, 0.1);

  &.sidebar-hidden {
    display: block !important; // Override the hidden class for mobile sidebar
    visibility: hidden;
    pointer-events: none;
  }

  @include media(mobile) {
    max-width: 100% !important;
  }
}
</style>
